import { cn } from "@/lib/cn";
import { Icons } from "@/components/ui/icons";
import { Button } from "@/components/ui/button";

/**
 * Workshop: Task queue item — agent avatar, title, source badge, status icon.
 *
 * Used in the Tasks tab of the SpacePanel. Shows queued agent work
 * with inline approval actions for waiting-approval tasks.
 */

type TaskStatus = "queued" | "running" | "blocked" | "waiting-approval";
type TaskSource = "chat" | "heartbeat" | "event" | "scheduled" | "comment" | "user";

const STATUS_CONFIG: Record<TaskStatus, { icon: React.ReactNode; color: string }> = {
  queued: { icon: <Icons.Circle className="h-3 w-3" />, color: "text-muted" },
  running: { icon: <Icons.Spinner className="h-3 w-3 animate-spin" />, color: "text-accent" },
  blocked: { icon: <Icons.AlertTriangle className="h-3 w-3" />, color: "text-warning" },
  "waiting-approval": { icon: <Icons.Eye className="h-3 w-3" />, color: "text-warning" },
};

const SOURCE_LABELS: Record<TaskSource, string> = {
  chat: "Chat",
  heartbeat: "Heartbeat",
  event: "Event",
  scheduled: "Scheduled",
  comment: "Comment",
  user: "User",
};

const SOURCE_COLORS: Record<TaskSource, string> = {
  chat: "text-accent",
  heartbeat: "text-muted",
  event: "text-warning",
  scheduled: "text-secondary",
  comment: "text-accent",
  user: "text-secondary",
};

interface TaskItemProps {
  /** Agent display letter */
  agentLetter: string;
  /** Agent avatar color */
  agentColor: string;
  /** Task title / request summary */
  title: string;
  /** Current status */
  status: TaskStatus;
  /** How the task was created */
  source: TaskSource;
  /** Agent name */
  agentName?: string;
  /** Approve callback (only for waiting-approval) */
  onApprove?: () => void;
  /** Reject callback (only for waiting-approval) */
  onReject?: () => void;
  /** Click callback */
  onClick?: () => void;
  className?: string;
  "data-testid"?: string;
}

export function TaskItem({
  agentLetter,
  agentColor,
  title,
  status,
  source,
  agentName,
  onApprove,
  onReject,
  onClick,
  className,
  "data-testid": testId,
}: TaskItemProps) {
  const statusConfig = STATUS_CONFIG[status];

  return (
    <div
      className={cn(
        "flex flex-col gap-1.5 border-b border-glass-stroke-subtle px-3 py-2.5",
        status === "waiting-approval" && "bg-warning/[0.04]",
        onClick && "cursor-pointer hover:bg-glass-stroke-subtle",
        className,
      )}
      onClick={onClick}
      data-testid={testId}
    >
      {/* Top row: agent avatar + title + status */}
      <div className="flex items-start gap-2">
        {/* Agent avatar */}
        <div
          className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[9px] font-bold text-white"
          style={{ backgroundColor: agentColor }}
        >
          {agentLetter}
        </div>

        {/* Title + meta */}
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="truncate text-[11px] font-medium text-primary">
            {title}
          </span>
          <div className="flex items-center gap-1.5">
            {agentName && (
              <span className="text-[9px] text-muted">{agentName}</span>
            )}
            <span className="text-[9px] text-muted">·</span>
            <span className={cn("text-[9px]", SOURCE_COLORS[source])}>{SOURCE_LABELS[source]}</span>
          </div>
        </div>

        {/* Status icon */}
        <div className={cn("mt-0.5 shrink-0", statusConfig.color)}>
          {statusConfig.icon}
        </div>
      </div>

      {/* Approval actions (inline) */}
      {status === "waiting-approval" && (
        <div className="flex items-center gap-1.5 pl-7">
          <Button
            variant="default"
            size="sm"
            onClick={(e) => { e.stopPropagation(); onApprove?.(); }}
            data-testid={testId ? `${testId}-approve` : undefined}
          >
            <Icons.Check className="h-3 w-3" />
            Approve
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => { e.stopPropagation(); onReject?.(); }}
            data-testid={testId ? `${testId}-reject` : undefined}
          >
            Reject
          </Button>
        </div>
      )}
    </div>
  );
}

export type { TaskItemProps, TaskStatus, TaskSource };
